import {
  BitkitActionMenu,
  BitkitButton,
  BitkitButtonProps,
  BitkitSpinner,
  BitkitSplitButton,
  BitkitTooltip,
  IconBlockCircle,
  IconBuild,
  IconChevronDown,
  IconDownload,
  IconEye,
  IconFileYml,
  IconRefresh,
  IconRemoteAccess,
  IconTrash,
  IconWorkflow,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';

import { segmentTrack } from '@/hooks/analytics/useTracking';

type ButtonProp = {
  isVisible: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick: VoidFunction;
};

type LinkProp = {
  href: string;
} & Omit<ButtonProp, 'onClick'>;

const DeleteLogButton = ({ isDisabled, isLoading, onClick }: Partial<ButtonProp>) => (
  <BitkitTooltip text="This build’s log has been deleted." disabled={!isDisabled}>
    <BitkitActionMenu.Item icon={IconTrash} isDanger disabled={isDisabled || isLoading} onClick={onClick}>
      <Box display="flex" gap="32" justifyContent="space-between" alignItems="center">
        <span>Delete logs</span>
        {isLoading && (
          <Box display="flex" gap="8" alignItems="center">
            <Text textStyle="body/md/regular">Preparing logs...</Text>
            <BitkitSpinner aria-hidden />
          </Box>
        )}
      </Box>
    </BitkitActionMenu.Item>
  </BitkitTooltip>
);

const DownloadLogButton = ({ isDisabled, isLoading, onClick }: Partial<ButtonProp>) => (
  <BitkitTooltip text="This build’s log has been deleted." disabled={!isDisabled || isLoading}>
    <BitkitActionMenu.Item icon={IconDownload} disabled={isDisabled || isLoading} onClick={onClick}>
      <Box display="flex" gap="32" justifyContent="space-between" alignItems="center">
        <span>Download logs</span>
        {isLoading && (
          <Box display="flex" gap="8" alignItems="center">
            <Text textStyle="body/md/regular">Preparing logs...</Text>
            <BitkitSpinner aria-hidden />
          </Box>
        )}
      </Box>
    </BitkitActionMenu.Item>
  </BitkitTooltip>
);

const ShowYmlButton = ({ isDisabled, isLoading, onClick }: Partial<ButtonProp>) => (
  <BitkitTooltip text="This build’s configuration YAML has been deleted." disabled={!isDisabled || isLoading}>
    <BitkitActionMenu.Item icon={IconEye} disabled={isDisabled || isLoading} onClick={onClick}>
      <Box display="flex" gap="32" justifyContent="space-between" alignItems="center">
        <span>View configuration YAML</span>
        {isLoading && <BitkitSpinner role="status" aria-label="Loading" />}
      </Box>
    </BitkitActionMenu.Item>
  </BitkitTooltip>
);

const DeleteYmlButton = ({ isDisabled, isLoading, onClick }: Partial<ButtonProp>) => (
  <BitkitTooltip text="This build’s configuration YAML has been deleted." disabled={!isDisabled || isLoading}>
    <BitkitActionMenu.Item icon={IconTrash} isDanger disabled={isDisabled || isLoading} onClick={onClick}>
      <Box display="flex" gap="32" justifyContent="space-between" alignItems="center">
        <span>Delete configuration YAML</span>
        {isLoading && <BitkitSpinner role="status" aria-label="Loading" />}
      </Box>
    </BitkitActionMenu.Item>
  </BitkitTooltip>
);

const RebuildWithRemoteAccessButton = ({ isLoading, onClick }: Pick<ButtonProp, 'isLoading' | 'onClick'>) => (
  <BitkitActionMenu.Item icon={IconRemoteAccess} disabled={isLoading} onClick={onClick}>
    Rebuild with Remote Access
  </BitkitActionMenu.Item>
);

const AbortButton = ({
  size,
  isLoading,
  onClick,
}: {
  size?: BitkitButtonProps['size'];
  isLoading?: boolean;
  onClick?: VoidFunction;
}) => (
  <BitkitButton
    variant="danger-secondary"
    state={isLoading ? 'loading' : undefined}
    flexGrow={4}
    onClick={onClick}
    size={size}
    icon={IconBlockCircle}
  >
    Abort
  </BitkitButton>
);

type BuildHeaderButtonsProps = {
  appSlug: string;
  buildSlug: string;
  isSticky: boolean;
  abortButton: ButtonProp;
  deleteLogButton: ButtonProp;
  downloadLogButton: ButtonProp;
  rebuildButton: ButtonProp;
  remoteAccessButton: ButtonProp;
  deleteYmlButton: ButtonProp;
  showYmlButton: ButtonProp;
  workflowEditorButton: LinkProp;
};

const BuildHeaderButtons = ({
  appSlug,
  buildSlug,
  abortButton: { isLoading: isAborting, isVisible: isAbortButtonVisible, onClick: onAbortClick },
  deleteLogButton,
  deleteYmlButton,
  downloadLogButton,
  rebuildButton: {
    isLoading: isRebuilding,
    isDisabled: isNotRebuildable,
    isVisible: isRebuildButtonVisible,
    onClick: onRebuildClick,
  },
  remoteAccessButton,
  showYmlButton,
  workflowEditorButton: { href: workFlowEditorHref, isVisible: isWorkflowEditorButtonVisible },
  isSticky,
}: BuildHeaderButtonsProps) => {
  const {
    isVisible: isDownloadLogButtonVisible,
    isLoading: isDownloadLogButtonLoading,
    isDisabled: isDownloadLogButtonDisabled,
  } = downloadLogButton;

  const {
    isVisible: isDeleteLogButtonVisible,
    isLoading: isDeleteLogButtonLoading,
    isDisabled: isDeleteLogButtonDisabled,
  } = deleteLogButton;

  const {
    isVisible: isDeleteYmlButtonVisible,
    isLoading: isDeleteYmlButtonLoading,
    isDisabled: isDeleteYmlButtonDisabled,
  } = deleteYmlButton;

  const {
    isVisible: isShowYmlButtonVisible,
    isLoading: isShowYmlButtonLoading,
    isDisabled: isShowYmlButtonDisabled,
  } = showYmlButton;

  const { isVisible: isRemoteAccessButtonVisible } = remoteAccessButton;

  const { isMobile } = useResponsive();
  const size = isSticky ? 'sm' : 'md';

  const trackWFEClick = () => {
    segmentTrack('Build Details Workflow Editor Clicked', {
      is_highlighted_button: false,
      app_slug: appSlug,
      build_slug: buildSlug,
    });
  };

  if (isMobile) {
    return (
      <>
        {(isWorkflowEditorButtonVisible ||
          isDeleteLogButtonVisible ||
          isDownloadLogButtonVisible ||
          isShowYmlButtonVisible ||
          isDeleteYmlButtonVisible ||
          isRebuildButtonVisible) && (
          <BitkitActionMenu
            trigger={
              <BitkitButton
                aria-label="Configuration YAML"
                flexGrow={1}
                size={size}
                suffixIcon={IconChevronDown}
                variant="secondary"
              >
                Actions
              </BitkitButton>
            }
          >
            {isWorkflowEditorButtonVisible && (
              <BitkitActionMenu.Item
                href={workFlowEditorHref}
                icon={IconWorkflow}
                key="open-workflow-editor"
                onClick={trackWFEClick}
              >
                Open Workflow Editor
              </BitkitActionMenu.Item>
            )}
            {isDownloadLogButtonVisible && <DownloadLogButton {...downloadLogButton} />}
            {isDeleteLogButtonVisible && <DeleteLogButton {...deleteLogButton} />}
            {isShowYmlButtonVisible && <ShowYmlButton {...showYmlButton} />}
            {isDeleteYmlButtonVisible && <DeleteYmlButton {...deleteYmlButton} />}
            {isRemoteAccessButtonVisible && <RebuildWithRemoteAccessButton {...remoteAccessButton} />}
          </BitkitActionMenu>
        )}

        {isAbortButtonVisible && <AbortButton size={size} isLoading={isAborting} onClick={onAbortClick} />}
        {isRebuildButtonVisible && (
          <BitkitButton
            disabledReason="Not rebuildable"
            state={isRebuilding ? 'loading' : isNotRebuildable ? 'disabled' : undefined}
            flexGrow={4}
            onClick={onRebuildClick}
            icon={IconRefresh}
            size={size}
          >
            Rebuild
          </BitkitButton>
        )}
      </>
    );
  }

  return (
    <>
      {isWorkflowEditorButtonVisible && (
        <BitkitButton
          icon={IconWorkflow}
          variant="secondary"
          href={workFlowEditorHref}
          size={size}
          onClick={trackWFEClick}
        >
          Edit Workflow
        </BitkitButton>
      )}
      {(isDownloadLogButtonVisible || isDeleteLogButtonVisible) && (
        <BitkitActionMenu
          size="md"
          trigger={
            <BitkitButton
              disabledReason={
                isDownloadLogButtonLoading || isDeleteLogButtonLoading
                  ? 'Preparing logs...'
                  : "This build's log has been deleted."
              }
              icon={IconBuild}
              size={size}
              state={
                (isDownloadLogButtonDisabled && isDeleteLogButtonDisabled) ||
                isDownloadLogButtonLoading ||
                isDeleteLogButtonLoading
                  ? 'disabled'
                  : undefined
              }
              suffixIcon={IconChevronDown}
              variant="secondary"
            >
              Logs
            </BitkitButton>
          }
        >
          {isDownloadLogButtonVisible && <DownloadLogButton {...downloadLogButton} />}
          {isDeleteLogButtonVisible && <DeleteLogButton {...deleteLogButton} />}
        </BitkitActionMenu>
      )}
      {(isShowYmlButtonVisible || isDeleteYmlButtonVisible) && (
        <BitkitActionMenu
          size="md"
          trigger={
            <BitkitButton
              disabledReason={
                isDeleteYmlButtonLoading || isShowYmlButtonLoading
                  ? 'Loading...'
                  : "This build's configuration YAML has been deleted."
              }
              aria-label="Configuration YAML"
              icon={IconFileYml}
              size={size}
              state={
                (isShowYmlButtonDisabled && isDeleteYmlButtonDisabled) ||
                isShowYmlButtonLoading ||
                isDeleteYmlButtonLoading
                  ? 'disabled'
                  : undefined
              }
              suffixIcon={IconChevronDown}
              variant="secondary"
            >
              Configuration YAML
            </BitkitButton>
          }
        >
          {isShowYmlButtonVisible && <ShowYmlButton {...showYmlButton} />}
          {isDeleteYmlButtonVisible && <DeleteYmlButton {...deleteYmlButton} />}
        </BitkitActionMenu>
      )}
      {isAbortButtonVisible && <AbortButton size={size} isLoading={isAborting} onClick={onAbortClick} />}
      {/* BitkitSplitButton always renders its menu half, so without the remote access action there is
          nothing to put behind the chevron and this falls back to a plain button. */}
      {isRebuildButtonVisible &&
        (isRemoteAccessButtonVisible ? (
          <BitkitTooltip disabled={!isNotRebuildable} text="Not rebuildable" wrapTrigger={isNotRebuildable}>
            <BitkitSplitButton size={size} state={isRebuilding ? 'loading' : isNotRebuildable ? 'disabled' : undefined}>
              <BitkitSplitButton.Action flexGrow={4} icon={IconRefresh} onClick={onRebuildClick}>
                Rebuild
              </BitkitSplitButton.Action>
              <BitkitSplitButton.Item
                disabled={remoteAccessButton.isLoading}
                icon={IconRemoteAccess}
                onClick={remoteAccessButton.onClick}
              >
                Rebuild with Remote Access
              </BitkitSplitButton.Item>
            </BitkitSplitButton>
          </BitkitTooltip>
        ) : (
          <BitkitButton
            disabledReason="Not rebuildable"
            state={isRebuilding ? 'loading' : isNotRebuildable ? 'disabled' : undefined}
            flexGrow={4}
            size={size}
            onClick={onRebuildClick}
            icon={IconRefresh}
          >
            Rebuild
          </BitkitButton>
        ))}
    </>
  );
};

export default BuildHeaderButtons;
